import type { LayoutItem } from '../../transformers/zod-schemas';
import { isWidgetRef } from '../../transformers/json-schema';
import { SIDE_PAD, INTERNAL_PRECISION, DISPLAY_PRECISION } from '../../constants';

export { SIDE_PAD } from '../../constants';
export { CSS_VAR_SIDE_PAD } from '../../constants';
export { DISPLAY_PRECISION } from '../../constants';

/**
 * Tolerance for float comparisons — matches INTERNAL_PRECISION so that values
 * cleaned by `cleanFloat` are treated as equal when within this margin.
 * Use `almostEqual` instead of `===` when comparing computed floats.
 */
export const EPSILON = 10 ** -INTERNAL_PRECISION;

export const cleanFloat = (n: number): number => parseFloat(n.toFixed(INTERNAL_PRECISION));
export const roundTick = (n: number): number => parseFloat(n.toFixed(DISPLAY_PRECISION));
export const almostEqual = (a: number, b: number): boolean => Math.abs(a - b) <= EPSILON;


/** Map a value to a CSS `left` percentage within the padded region.
 *  Clamps to [SIDE_PAD, 100-SIDE_PAD] so out-of-range values never escape the axis.
 */
export const toPercent = (value: number, min: number, max: number): number => {
  const raw = SIDE_PAD + ((value - min) / (max - min)) * (100 - 2 * SIDE_PAD);
  return Math.max(SIDE_PAD, Math.min(100 - SIDE_PAD, raw));
};

/**
 * Convert a chip's pixel X position to its axis value.
 * Exact inverse of `toPercent`.
 */
export const pixelToValue = (
  chipCenterX: number,
  axisLeft: number,
  axisWidth: number,
  min: number,
  max: number,
): number => {
  const relX     = (chipCenterX - axisLeft) / axisWidth;
  const fraction = (relX - SIDE_PAD / 100) / (1 - 2 * SIDE_PAD / 100);
  const clamped  = Math.max(0, Math.min(1, fraction));
  return min + clamped * (max - min);
};

/**
 * Build ordered major tick values for [min, max] at the given step.
 * `max` is always included as the last tick even if not a step multiple.
 */
export const getMajorTicks = (min: number, max: number, step: number): number[] => {
  if (step <= 0 || max <= min) return [min, max];
  const count = Math.floor((max - min) / step);
  const ticks = Array.from({ length: count + 1 }, (_, i) => cleanFloat(min + i * step));
  const lastTick = ticks[ticks.length - 1]!;
  // `almostEqual` is intentionally not used here — the concern is visual duplicates at
  // DISPLAY_PRECISION, not internal float noise. Two ticks that look identical on screen
  // (same rounded label) must collapse to one, even when they differ by more than EPSILON.
  if (roundTick(lastTick) !== roundTick(max)) {
    // max is visually distinct — append it as an extra tick
    ticks.push(max);
  } else {
    // Same display value — snap to the canonical max
    ticks[ticks.length - 1] = max;
  }
  return ticks;
};

/**
 * Flat array of minor tick values across the entire axis.
 * Shared by rendering and snap computation so they stay consistent.
 */
export const getMinorTickValues = (majorTicks: number[], minorTicks: number): number[] => {
  if (minorTicks <= 0 || majorTicks.length < 2) return [];
  const result: number[] = [];
  for (let i = 0; i < majorTicks.length - 1; i++) {
    const from     = majorTicks[i]!;
    const to       = majorTicks[i + 1]!;
    const interval = to - from;
    for (let j = 1; j <= minorTicks; j++) {
      result.push(cleanFloat(from + (j / (minorTicks + 1)) * interval));
    }
  }
  return result;
};

/** Full snap candidate list: major + all minor ticks. */
export const getSnapTicks = (majorTicks: number[], minorTicks: number): number[] => [
  ...majorTicks,
  ...getMinorTickValues(majorTicks, minorTicks),
];

/**
 * Snap `raw` to the nearest tick value.
 * Returns `cleanFloat(raw)` when `ticks` is empty (no-snap fallback).
 * Ticks are assumed to be already cleaned — no double-rounding applied.
 */
export const snapToNearest = (raw: number, ticks: number[]): number => {
  if (ticks.length === 0) return cleanFloat(raw);
  return ticks.reduce((best, t) => Math.abs(t - raw) < Math.abs(best - raw) ? t : best);
};

// ─── Widget resolution ─────────────────────────────────────────────────────────

/** Return the `widgetId` of the first widget-ref in a position's content, or `null`. */
export const getPositionWidgetId = (content: LayoutItem[]): string | null => {
  for (const item of content) {
    if (isWidgetRef(item)) return item.widgetId;
  }
  return null;
};

// ─── Number-line overlays ──────────────────────────────────────────────────────

/**
 * Returns the CSS custom property call for a highlight color token.
 * e.g. `'blue'` → `'var(--nl-highlight-blue)'`
 */
export const toHighlightColor = (color: string): string =>
  `var(--nl-highlight-${color})`;
